import React, { useCallback, useEffect, useState } from 'react';
import { useApi } from '../../hooks/useApi';
import { useWebSocket } from '../../hooks/useWebSocket';
import { timeAgo } from '../chat/format';
import { ORIGIN } from '../chat/status';

/**
 * Cola de casos (FIFO), carga por asesor y reasignación.
 */
export default function WorkloadPanel() {
  const { apiFetch } = useApi();
  const [queue, setQueue] = useState([]);
  const [users, setUsers] = useState([]);
  const [orphaned, setOrphaned] = useState([]);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const [q, w] = await Promise.all([apiFetch('/api/admin/queue'), apiFetch('/api/admin/workload')]);
      setQueue(q);
      setUsers(w.users);
      setOrphaned(w.orphaned);
      setError('');
    } catch (e) {
      setError(e.message);
    }
  }, [apiFetch]);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  // Se actualiza al instante con cada asignación o cierre
  useWebSocket(useCallback((msg) => {
    if (['conversation_assigned', 'conversation_closed', 'conversation_queued', 'conversation_requeued', 'presence_changed'].includes(msg.type)) load();
  }, [load]));

  async function assign(sessionId, userId) {
    try {
      await apiFetch(`/api/conversations/${sessionId}/assign`, { method: 'POST', body: JSON.stringify({ user_id: userId }) });
      load();
    } catch (e) {
      alert('No se pudo asignar: ' + e.message);
    }
  }

  const phone = (sid) => sid.split('_').slice(1).join('_');

  return (
    <div style={s.wrap}>
      {error && <div style={s.errorBox}>⚠️ {error}</div>}

      <section style={s.card}>
        <h2 style={s.h2}>⏳ En cola <span style={s.count}>{queue.length}</span></h2>
        {queue.length === 0 && <div style={s.empty}>No hay casos esperando.</div>}
        {queue.map(c => (
          <div key={c.session_id} style={s.row}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={s.phone}>{phone(c.session_id)} <span style={s.muted}>· espera {timeAgo(c.queued_at || c.last_message_at)}</span></div>
              <div style={s.muted}>
                {c.origin ? ORIGIN[c.origin] || c.origin : 'Sin origen'}{c.escalation_reason ? ` · ${c.escalation_reason}` : ''}
              </div>
              {c.escalation_summary && <div style={s.summary}>{c.escalation_summary}</div>}
            </div>
            <AssignSelect users={users} onPick={uid => assign(c.session_id, uid)} label="Asignar a…" />
          </div>
        ))}
      </section>

      {orphaned.length > 0 && (
        <section style={{ ...s.card, borderColor: 'var(--danger-border)' }}>
          <h2 style={s.h2}>⚠️ Asignadas a usuarios desactivados <span style={s.count}>{orphaned.length}</span></h2>
          <div style={s.muted}>Nadie las está atendiendo: reasígnalas.</div>
          {orphaned.map(c => (
            <div key={c.session_id} style={s.row}>
              <div style={{ flex: 1 }}>{phone(c.session_id)} <span style={s.muted}>· asignada {timeAgo(c.assigned_at)}</span></div>
              <AssignSelect users={users} onPick={uid => assign(c.session_id, uid)} label="Reasignar a…" />
            </div>
          ))}
        </section>
      )}

      <section style={s.card}>
        <h2 style={s.h2}>👥 Carga por asesor <span style={{ ...s.muted, fontWeight: 400 }}>· reparto automático entre los Disponibles del balanceador que reciben casos</span></h2>
        {users.map(u => (
          <div key={u.id} style={s.userBlock}>
            <div style={s.userHead}>
              <PresenceDot presence={u.presence} since={u.presence === 'offline' ? u.disconnected_at : u.paused_at} />
              <strong>{u.name}</strong>
              <span style={s.muted}>{u.role === 'admin' ? 'Admin' : 'Asesor'} · {u.agent_type_label}{!u.receives_assignments ? ' · no recibe casos' : ''}</span>
              <span style={{ ...s.count, marginLeft: 'auto' }}>{u.assigned_count} abiertas</span>
            </div>
            {u.conversations.map(c => (
              <div key={c.session_id} style={{ ...s.row, paddingLeft: 12 }}>
                <div style={{ flex: 1 }}>
                  {phone(c.session_id)} <span style={s.muted}>· asignada {timeAgo(c.assigned_at)} · último mensaje {timeAgo(c.last_message_at)}</span>
                </div>
                <AssignSelect
                  users={users.filter(x => x.id !== u.id)}
                  onPick={uid => assign(c.session_id, uid)}
                  label="Reasignar a…"
                />
              </div>
            ))}
          </div>
        ))}
      </section>
    </div>
  );
}

const PRESENCE = {
  online: { color: 'var(--success)', label: 'Disponible' },
  paused: { color: 'var(--warning)', label: 'En pausa' },
  offline: { color: 'var(--text-3)', label: 'Desconectado' },
};

function PresenceDot({ presence, since }) {
  const p = PRESENCE[presence] || PRESENCE.offline;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, color: p.color, minWidth: 110 }}
      title={since ? `desde hace ${timeAgo(since)}` : undefined}>
      <span style={{ width: 8, height: 8, borderRadius: '50%', background: p.color }} />
      {p.label}{since && presence !== 'online' ? ` · ${timeAgo(since)}` : ''}
    </span>
  );
}

function AssignSelect({ users, onPick, label }) {
  return (
    <select style={s.select} value="" onChange={e => e.target.value && onPick(Number(e.target.value))}>
      <option value="">{label}</option>
      {users.map(u => (
        // Sin "Recibe casos" no se le pueden asignar casos
        <option key={u.id} value={u.id} disabled={!u.receives_assignments}>
          {u.name} · {u.assigned_count} abiertas{u.receives_assignments ? '' : ' · no recibe casos'}
        </option>
      ))}
    </select>
  );
}

const s = {
  wrap: { display: 'flex', flexDirection: 'column', gap: 16 },
  card: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: 14, display: 'flex', flexDirection: 'column', gap: 8 },
  h2: { fontSize: 15, fontWeight: 800, color: 'var(--text)', margin: 0, display: 'flex', alignItems: 'center', gap: 8 },
  count: { fontSize: 11, fontWeight: 700, background: 'var(--accent-soft)', color: 'var(--accent-text)', borderRadius: 10, padding: '2px 8px' },
  empty: { color: 'var(--text-3)', fontSize: 13 },
  row: { display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0', borderTop: '1px solid var(--border)', fontSize: 13 },
  phone: { fontWeight: 700, color: 'var(--text)' },
  muted: { color: 'var(--text-3)', fontSize: 12 },
  summary: { color: 'var(--warning)', fontSize: 12, marginTop: 4 },
  userBlock: { borderTop: '1px solid var(--border)', paddingTop: 8 },
  userHead: { display: 'flex', alignItems: 'center', gap: 10, fontSize: 13 },
  select: { background: 'var(--raised)', border: '1px solid var(--border-strong)', borderRadius: 8, padding: '6px 10px', color: 'var(--text)', fontSize: 12 },
  errorBox: { background: 'var(--danger-soft)', border: '1px solid var(--danger-border)', borderRadius: 10, padding: '10px 14px', color: 'var(--danger)', fontSize: 13 },
};
